import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { LocationQuery } from 'vue-router'
import * as apiRequest from '@shared/api-endpoints'
import Tracker from '@shared/Tracker'
import env from '@shared/env.js'
import { localStorage, sessionStorage } from '@shared/helpers/storage'
import type { StorageLike } from 'pinia-plugin-persistedstate'
import { isFromApp } from '@shared/helpers/isFromApp'

type FeatureConfigEntry = {
  defaultEnabled: boolean
  devOverride?: boolean | null
}

// FeatureFlags need to be defined here
export const FEATURE_CONFIG = {
  doctorSearchUsingVzD: {
    defaultEnabled: false,
    devOverride: null,
  },
  webOptimizedFunnel: {
    defaultEnabled: false,
    devOverride: null,
  },
  insuranceSelectionRedesign: {
    defaultEnabled: false,
    devOverride: null,
  },
  // Web counterpart of the backend passwordless_signup flag, which the API
  // checks too; MSK EU only. Rolled out through its pre-signup experiment below.
  passwordlessSignup: {
    defaultEnabled: false,
    devOverride: null,
  },
} as const satisfies Record<string, FeatureConfigEntry>

type PreSignupVariant = {
  variantKey: string
  featureFlagEnabled: boolean
  ratio: number
}

type PreSignupExperiment = {
  featureFlagKey: FeatureFlagKey
  experimentKey: string
  isEligible: () => boolean
  variants: PreSignupVariant[]
}

// Experiments whose variant is drawn client-side before signup and submitted at registration.
// Variant ratios must sum to 1. Each experiment must have pre_signup: true in the backend config.
export const PRE_SIGNUP_EXPERIMENTS: PreSignupExperiment[] = [
  {
    featureFlagKey: 'webOptimizedFunnel',
    experimentKey: 'web_optimized_funnel_webcheckout_msk_eu_v1',
    isEligible: () => env.isMskEu && !isFromApp(),
    // The no-welcome variant of the closed A/A test keeps its label in analytics.
    variants: [
      {
        variantKey: 'a_enabled_without_welcome',
        featureFlagEnabled: true,
        ratio: 1,
      },
    ],
  },
  {
    featureFlagKey: 'insuranceSelectionRedesign',
    experimentKey: 'insurance_selection_redesign_webcheckout_msk_eu_v1',
    isEligible: () => env.isMskEu && !isFromApp(),
    variants: [
      { variantKey: 'a_enabled', featureFlagEnabled: true, ratio: 0.5 },
      { variantKey: 'b_disabled', featureFlagEnabled: false, ratio: 0.5 },
    ],
  },
  {
    featureFlagKey: 'passwordlessSignup',
    experimentKey: 'passwordless_signup_webcheckout_msk_eu_v1',
    isEligible: () => env.isMskEu && !isFromApp(),
    // Raise a_enabled only once the backend passwordless_signup flag is on in
    // production; until then the API rejects passwordless sign-ups.
    variants: [
      { variantKey: 'a_enabled', featureFlagEnabled: true, ratio: 0 },
      { variantKey: 'b_disabled', featureFlagEnabled: false, ratio: 1 },
    ],
  },
]

export type FeatureFlagKey = keyof typeof FEATURE_CONFIG
/** Effective on/off state of every configured feature flag */
export type FeatureFlags = Record<FeatureFlagKey, boolean>
type RemoteFeatureFlag = {
  featureFlagKey: string
  enabled: boolean
  experimentKey: string | null
  variantKey: string | null
}

type FlagEntry = {
  featureFlagKey: FeatureFlagKey
  defaultEnabled: boolean
  devOverride: boolean | null
  remoteEnabled: boolean | null
  remoteExperimentKey: string | null
  remoteVariantKey: string | null
  localEnabled: boolean | null
}

const FEATURE_KEYS = Object.keys(FEATURE_CONFIG) as FeatureFlagKey[]
const RESET_OVERRIDE = 'default' as const
const useRemoteConfig = env.VITE_FEATURE_FLAGS_MODE !== 'test'
const canOverrideFlags =
  import.meta.env.DEV ||
  import.meta.env.MODE === 'test' ||
  env.VITE_MODE === 'dev' ||
  env.VITE_MODE === 'stage'

const getEffectiveEnabled = (entry: FlagEntry): boolean =>
  (canOverrideFlags ? entry.localEnabled : null) ??
  (canOverrideFlags ? entry.devOverride : null) ??
  (useRemoteConfig ? entry.remoteEnabled : null) ??
  entry.defaultEnabled

const getResponseStatus = (error: unknown): number | undefined => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { status?: unknown } }).response?.status ===
      'number'
  ) {
    return (error as { response?: { status?: number } }).response?.status
  }
  return undefined
}

const toFlagEntry = (featureFlagKey: FeatureFlagKey): FlagEntry => ({
  featureFlagKey,
  defaultEnabled: FEATURE_CONFIG[featureFlagKey].defaultEnabled,
  devOverride: FEATURE_CONFIG[featureFlagKey].devOverride ?? null,
  remoteEnabled: null,
  remoteExperimentKey: null,
  remoteVariantKey: null,
  localEnabled: null,
})

const createInitialFlagMap = (): Record<FeatureFlagKey, FlagEntry> =>
  FEATURE_KEYS.reduce(
    (result, featureFlagKey) => {
      result[featureFlagKey] = toFlagEntry(featureFlagKey)
      return result
    },
    {} as Record<FeatureFlagKey, FlagEntry>,
  )

export const useFeatureFlagStore = defineStore(
  'featureFlags',
  () => {
    const flagsByKey = ref<Record<FeatureFlagKey, FlagEntry>>(
      createInitialFlagMap(),
    )

    const preSignupAssignments = ref<RemoteFeatureFlag[]>([])

    const resetRemoteFlags = () => {
      FEATURE_KEYS.forEach((featureFlagKey) => {
        const feature = flagsByKey.value[featureFlagKey]
        feature.remoteEnabled = null
        feature.remoteExperimentKey = null
        feature.remoteVariantKey = null
      })
    }

    const applyRemoteFlags = (remoteFeatureFlags: RemoteFeatureFlag[]) => {
      resetRemoteFlags()

      remoteFeatureFlags.forEach((remoteFlag: RemoteFeatureFlag) => {
        if (
          !Object.prototype.hasOwnProperty.call(
            flagsByKey.value,
            remoteFlag.featureFlagKey,
          )
        ) {
          return
        }

        const featureKey = remoteFlag.featureFlagKey as FeatureFlagKey
        const feature = flagsByKey.value[featureKey]
        feature.remoteEnabled = remoteFlag.enabled
        feature.remoteExperimentKey = remoteFlag.experimentKey
        feature.remoteVariantKey = remoteFlag.variantKey
      })
    }

    const setOverride = (
      featureFlagKey: FeatureFlagKey,
      enabled: boolean | null,
    ) => (flagsByKey.value[featureFlagKey].localEnabled = enabled)

    const setPreSignupVariantOverride = (
      featureFlagKey: FeatureFlagKey,
      variantKey: string | null,
    ) => {
      const experiment = PRE_SIGNUP_EXPERIMENTS.find(
        (e) => e.featureFlagKey === featureFlagKey,
      )
      if (!experiment) return

      const variant =
        experiment.variants.find((v) => v.variantKey === variantKey) ?? null

      flagsByKey.value[featureFlagKey].localEnabled =
        variant?.featureFlagEnabled ?? null
      preSignupAssignments.value = [
        ...preSignupAssignments.value.filter(
          (a) => a.featureFlagKey !== featureFlagKey,
        ),
        ...(variant
          ? [
              {
                featureFlagKey,
                enabled: variant.featureFlagEnabled,
                experimentKey: experiment.experimentKey,
                variantKey: variant.variantKey,
              },
            ]
          : []),
      ]
    }

    const applyQueryOverrides = (query: LocationQuery) => {
      FEATURE_KEYS.forEach((featureFlagKey) => {
        const rawQueryValue = query[`ff_${featureFlagKey}`]
        const queryValue = Array.isArray(rawQueryValue)
          ? rawQueryValue[0]
          : rawQueryValue

        if (typeof queryValue !== 'string') {
          return
        }

        const normalized = queryValue.trim().toLowerCase()
        const override =
          normalized === RESET_OVERRIDE ? null : Boolean(normalized === 'true')

        setOverride(featureFlagKey, override)
      })
    }

    const flags = computed<FeatureFlags>(() =>
      FEATURE_KEYS.reduce(
        (result, featureFlagKey) => {
          result[featureFlagKey] = getEffectiveEnabled(
            flagsByKey.value[featureFlagKey],
          )
          return result
        },
        {} as Record<FeatureFlagKey, boolean>,
      ),
    )

    const getEffectiveFeatureFlagsForReporting = () =>
      FEATURE_KEYS.map((featureFlagKey) => ({
        featureFlagKey,
        enabled: getEffectiveEnabled(flagsByKey.value[featureFlagKey]),
      }))

    const trackFeatureFlagsAndExperiments = () => {
      const customPayload: Record<string, boolean | string> = {}
      const effectiveFeatureFlags = getEffectiveFeatureFlagsForReporting()

      effectiveFeatureFlags.forEach((featureFlag) => {
        customPayload[`feature_flag_${featureFlag.featureFlagKey}`] =
          featureFlag.enabled
      })

      FEATURE_KEYS.forEach((featureFlagKey) => {
        const featureFlag = flagsByKey.value[featureFlagKey]
        const { remoteExperimentKey, remoteVariantKey } = featureFlag
        if (remoteExperimentKey == null || remoteVariantKey == null) return
        customPayload[`experiment_${remoteExperimentKey}`] = remoteVariantKey
      })

      Tracker.trackKaiaEvent(
        {
          event_name: 'c.lfcycl.feature_flags_updated',
          action: 'update',
          object_type: 'feature_flags',
          screen_name: 'signup',
        },
        customPayload,
      )
    }

    const drawVariant = (variants: PreSignupVariant[]): PreSignupVariant => {
      const rand = Math.random()
      let cumulative = 0
      for (const variant of variants) {
        cumulative += variant.ratio
        if (rand < cumulative) return variant
      }
      return variants[variants.length - 1]
    }

    // A persisted assignment survives only while its experiment is eligible and
    // still allocates that variant; anything else (a closed variant, one set to
    // 0%, a renamed experiment) is drawn afresh so the browser follows the
    // current allocation. Setting a variant to 0% therefore also acts as its kill switch.
    const isCurrentAssignment = (assignment: RemoteFeatureFlag) =>
      PRE_SIGNUP_EXPERIMENTS.some(
        (experiment) =>
          experiment.featureFlagKey === assignment.featureFlagKey &&
          experiment.experimentKey === assignment.experimentKey &&
          experiment.isEligible() &&
          experiment.variants.some(
            (variant) =>
              variant.variantKey === assignment.variantKey && variant.ratio > 0,
          ),
      )

    const assignPreSignupFlags = () => {
      const kept = preSignupAssignments.value.filter(isCurrentAssignment)
      const drawn: RemoteFeatureFlag[] = PRE_SIGNUP_EXPERIMENTS.filter(
        (experiment) =>
          experiment.isEligible() &&
          !kept.some((a) => a.featureFlagKey === experiment.featureFlagKey),
      ).map((experiment) => {
        const variant = drawVariant(experiment.variants)
        return {
          featureFlagKey: experiment.featureFlagKey,
          enabled: variant.featureFlagEnabled,
          experimentKey: experiment.experimentKey,
          variantKey: variant.variantKey,
        }
      })

      preSignupAssignments.value = [...kept, ...drawn]
      applyRemoteFlags(preSignupAssignments.value)
    }

    const getPreSignupPayload = () =>
      preSignupAssignments.value.map((a) => ({
        feature_flag_key: a.featureFlagKey,
        feature_flag_enabled: a.enabled,
        experiment_key: a.experimentKey,
        variant_key: a.variantKey,
      }))

    const loadRemoteFeatureFlags = async () => {
      try {
        const remoteFeatureFlags = await apiRequest.getUserFeatureFlags()
        const normalizedFeatureFlags = Array.isArray(remoteFeatureFlags)
          ? remoteFeatureFlags
          : []

        applyRemoteFlags(normalizedFeatureFlags)
        preSignupAssignments.value = []

        trackFeatureFlagsAndExperiments()

        try {
          await apiRequest.setUserEffectiveFeatureFlags(
            getEffectiveFeatureFlagsForReporting(),
          )
        } catch (error: unknown) {
          console.warn(
            '[feature-flags-sync] webcheckout_effective_flags_post_failure',
            {
              metric_name: 'webcheckout_effective_flags_post_failure',
              status_code: getResponseStatus(error),
            },
          )
        }
      } catch (error: unknown) {
        console.warn(
          '[feature-flags-sync] webcheckout_feature_flags_fetch_failure',
          {
            metric_name: 'webcheckout_feature_flags_fetch_failure',
            status_code: getResponseStatus(error),
          },
        )
      }
    }

    return {
      flags,
      flagKeys: FEATURE_KEYS,
      flagsByKey,
      canOverrideFlags,
      preSignupAssignments,
      loadRemoteFeatureFlags,
      assignPreSignupFlags,
      getPreSignupPayload,
      applyQueryOverrides,
      setOverride,
      setPreSignupVariantOverride,
      resetRemoteFlags,
    }
  },
  {
    persist: [
      {
        storage: localStorage as unknown as StorageLike,
        pick: ['preSignupAssignments'],
      },
      {
        storage: sessionStorage as unknown as StorageLike,
        pick: ['flagsByKey'],
      },
    ],
  },
)

export default useFeatureFlagStore
